/**
 * Weapon definitions — docs/09-weapons.md.
 * Each category has 2-4 weapons with a drop rarity. Mount layout is a
 * GLOBAL ship tier (gun tier card): nose → wings → wings+nose → +top
 * supershot cannon. Weapon swaps keep the tier.
 */
export type WeaponCategory = 'bullet' | 'plasma' | 'laser' | 'bomb';
export type WeaponRarity = 'common' | 'rare' | 'epic';
export type ProjectileBehavior = 'straight' | 'decel' | 'homing';

export interface Mount {
  /** Forward offset in ship-local space (px) */
  forward: number;
  /** Side offset (+ right, - left) */
  side: number;
  /** Angle deviation added to aim direction (deg) */
  angleDeg: number;
}

export interface ProjectileDef {
  speed: number;
  life: number;
  radius: number;
  damage: number;
  texKey: string;
  rotates: boolean;
  behavior: ProjectileBehavior;
  /** Drag coefficient for 'decel' (exponential slowdown) */
  drag?: number;
  /** Steering rate for 'homing' (rad/s) */
  turnRate?: number;
  /** Area damage on expiry/impact */
  aoeRadius?: number;
  aoeDamage?: number;
}

export interface WeaponDef {
  id: string;
  category: WeaponCategory;
  nameKey: string;
  rarity: WeaponRarity;
  fireRate: number; // shots/s (all mounts fire together)
  projectile: ProjectileDef;
}

const straight = (
  p: Omit<ProjectileDef, 'behavior'>,
): ProjectileDef => ({ ...p, behavior: 'straight' });

export const WEAPONS: Record<string, WeaponDef> = {
  // ---- bullet
  autocannon: {
    id: 'autocannon', category: 'bullet', nameKey: 'weapon.autocannon', rarity: 'common',
    fireRate: 9,
    projectile: straight({ speed: 950, life: 1.1, radius: 7, damage: 10, texKey: 'bullet/player/basic', rotates: true }),
  },
  smg: {
    id: 'smg', category: 'bullet', nameKey: 'weapon.smg', rarity: 'common',
    fireRate: 16,
    projectile: straight({ speed: 1050, life: 0.7, radius: 6, damage: 5, texKey: 'bullet/player/basic', rotates: true }),
  },
  heavy_cannon: {
    id: 'heavy_cannon', category: 'bullet', nameKey: 'weapon.heavy_cannon', rarity: 'rare',
    fireRate: 3.5,
    projectile: straight({ speed: 820, life: 1.4, radius: 11, damage: 32, texKey: 'bullet/player/basic', rotates: true }),
  },

  // ---- plasma
  plasma_orb: {
    id: 'plasma_orb', category: 'plasma', nameKey: 'weapon.plasma_orb', rarity: 'common',
    fireRate: 4.5,
    projectile: {
      speed: 620, life: 1.4, radius: 13, damage: 24, texKey: 'bullet/player/plasma',
      rotates: false, behavior: 'straight', aoeRadius: 48, aoeDamage: 7,
    },
  },
  plasma_lance: {
    id: 'plasma_lance', category: 'plasma', nameKey: 'weapon.plasma_lance', rarity: 'rare',
    fireRate: 6.5,
    projectile: {
      speed: 1000, life: 1.0, radius: 9, damage: 17, texKey: 'bullet/player/plasma',
      rotates: false, behavior: 'straight', aoeRadius: 38, aoeDamage: 5,
    },
  },

  // ---- laser
  needle_laser: {
    id: 'needle_laser', category: 'laser', nameKey: 'weapon.needle_laser', rarity: 'common',
    fireRate: 14,
    projectile: straight({ speed: 1500, life: 0.8, radius: 5, damage: 4.5, texKey: 'bullet/player/laser', rotates: true }),
  },
  pulse_laser: {
    id: 'pulse_laser', category: 'laser', nameKey: 'weapon.pulse_laser', rarity: 'rare',
    fireRate: 10,
    projectile: straight({ speed: 1300, life: 1.0, radius: 7, damage: 9, texKey: 'bullet/player/laser', rotates: true }),
  },

  // ---- bomb
  mortar: {
    id: 'mortar', category: 'bomb', nameKey: 'weapon.mortar', rarity: 'common',
    fireRate: 0.9,
    projectile: {
      speed: 560, life: 0.85, radius: 14, damage: 34, texKey: 'bullet/player/bomb',
      rotates: false, behavior: 'decel', drag: 2.4, aoeRadius: 75, aoeDamage: 40,
    },
  },
  rocket: {
    id: 'rocket', category: 'bomb', nameKey: 'weapon.rocket', rarity: 'rare',
    fireRate: 1.0,
    projectile: {
      speed: 760, life: 0.95, radius: 12, damage: 48, texKey: 'bullet/player/bomb',
      rotates: true, behavior: 'straight', aoeRadius: 85, aoeDamage: 36,
    },
  },
  homing_missile: {
    id: 'homing_missile', category: 'bomb', nameKey: 'weapon.homing_missile', rarity: 'epic',
    fireRate: 0.9,
    projectile: {
      speed: 540, life: 1.8, radius: 11, damage: 40, texKey: 'bullet/player/missile',
      rotates: true, behavior: 'homing', turnRate: 4.2, aoeRadius: 60, aoeDamage: 26,
    },
  },
};

export const STARTING_WEAPON = 'autocannon';
export const MAX_GUN_TIER = 4;

/** Up to 3 weapons fire together (docs/12 G3); each keeps its OWN cooldown. */
export const MAX_ACTIVE_WEAPONS = 3;
/** Global fire-rate penalty by active weapon count (1 / 2 / 3 weapons) */
export const RACK_PENALTY: ReadonlyArray<number> = [1, 0.8, 0.65];

/**
 * Base projectile lifetime multiplier — default range is deliberately SHORT
 * so the Range card line is a real build decision (docs/12 D2).
 */
export const RANGE_BASE_MUL = 0.65;

/** Gun tier → normal-fire mounts. Tier 4 keeps tier 3 mounts and adds the top supershot cannon. */
export const MOUNTS_BY_TIER: ReadonlyArray<ReadonlyArray<Mount>> = [
  [{ forward: 24, side: 0, angleDeg: 0 }],                                          // T1: nose
  [{ forward: 10, side: -15, angleDeg: 0 }, { forward: 10, side: 15, angleDeg: 0 }], // T2: wings
  [
    { forward: 24, side: 0, angleDeg: 0 },
    { forward: 10, side: -15, angleDeg: 0 }, { forward: 10, side: 15, angleDeg: 0 },
  ],                                                                                 // T3: nose + wings
  [
    { forward: 24, side: 0, angleDeg: 0 },
    { forward: 10, side: -15, angleDeg: 0 }, { forward: 10, side: 15, angleDeg: 0 },
  ],                                                                                 // T4: + top cannon (supershot)
];

/** Top-center supershot cannon mount, unlocked at tier 4 */
export const TOP_MOUNT: Mount = { forward: -4, side: 0, angleDeg: 0 };

export const SUPERSHOT = {
  fanCount: 5,
  spreadDeg: 36,
  damageMul: 3,
  scale: 1.25,
  /** Extra multipliers when fired from the tier-4 top cannon */
  topCannonDamageMul: 1.5,
  topCannonScale: 1.4,
  /** Passive energy trickle (units/s) — main fill comes from landing hits */
  energyRegen: 4,
} as const;
